import * as React from 'react'
import { AdminIcon } from '@/modules/shared/components'
import moment from 'moment'
import type { Moment } from 'moment'
import FlexView from '@/modules/shared/components/FlexView'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { PortalPopover } from './PortalPopover'

import { EventModel } from '@/app/models/newDelegation/eventModel'
import { matchEvent } from '@/app/common/newDelegation/eventIdentity'
import {
  findOpenLoading,
  findPrecedingCountryChange,
  isTmsEvent,
} from '@/modules/drivers/utils/eventTmsPair'
import { getTripEventTypeName } from '@/app/common/converters/newDelegation/tripEventTypeToTextConverter'
import { DateField } from '@/ui/form/controls/DateField'
import { TimeField } from '@/ui/form/controls/TimeField'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { getCountryFlag } from '@/app/services/country/countryFlagResolver'
import { TIME_FORMAT } from '@/app/constants/date'
import { determineRouteType } from '@/app/common/tmsRouteTypeHelper'
import { TmsRouteType } from '@/app/constants/tmsRouteType'
import { getTmsRouteTypeName } from '@/app/services/tms/tmsRouteTypeNameResolver'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { translateCountryName } from '@/app/common/countryNames'
import { getTmsRouteTypeColor } from '@/app/services/tms/tmsRouteTypeColorResolver'
import { FaIcon, faTrash } from '@/lib/fa'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import CheckboxWithLabelField from '@/modules/drivers/forms/contract/inputs/CheckboxWithLabelField'
import { useParams } from 'react-router-dom'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { useQueryConfigurationBusinessTrips } from '@/lib/api/company/configuration/businessTrips/useQueryConfigurationBusinessTrips'

type DelegationMarkerPopupProps = {
  delegationMarker: EventModel
  countries: Array<CountryModel>
  events: Array<EventModel>
  allPreviousTripEvents: Array<EventModel>
  id: string
  isOpen: boolean
  translate: (text: string) => string
  closePopup: (
    delegationMarker: EventModel,
    shouldEdit: boolean,
    isChanged: boolean,
    newMarkers: Array<EventModel>,
  ) => void
  onDelete: (delegationMarker: EventModel) => void
  readOnly: boolean
}

// Validation helpers
const isDocNumberNotEmpty = (
  docNumber: string | null,
  eventType: TripEventType,
): boolean => {
  return (
    (eventType !== TripEventType.Loading &&
      eventType !== TripEventType.Unloading) ||
    !!docNumber
  )
}

// WPF parity (DelegationMarkerInfoDialog.ChangeCommandExecute): a document may hold
// two TMS markers, the edited one included. A number newly given to a marker must not
// already belong to one of the same type - such a document could never be paired.
const isDocNumberValid = (
  docNumber: string | null,
  eventType: TripEventType,
  events: Array<EventModel>,
  marker: EventModel,
): boolean => {
  if (
    (eventType !== TripEventType.Loading &&
      eventType !== TripEventType.Unloading) ||
    !docNumber
  ) {
    return true
  }

  const others = events.filter(
    (e) =>
      e.docNumber === docNumber &&
      !e.isNew &&
      !matchEvent(e, marker) &&
      isTmsEvent(e),
  )
  const assigned = docNumber !== marker.docNumber
  return (
    others.length < 2 &&
    !(assigned && others.some((e) => e.tripEvent === eventType))
  )
}

export const DelegationMarkerPopup: React.FC<DelegationMarkerPopupProps> = ({
  delegationMarker,
  countries: countriesProp,
  events: propsEvents,
  allPreviousTripEvents,
  id,
  isOpen,
  translate,
  closePopup,
  onDelete,
  readOnly,
}) => {
  const { activeLanguage } = useLocalize()
  const lang = activeLanguage?.code || 'pl'
  const { companyId } = useParams<{ companyId: string }>()
  const { data: fetchedCountries = [] } = useQueryCountries({ companyId })
  const { data: businessTripConfig } =
    useQueryConfigurationBusinessTrips(companyId)
  const priorityCountryCodes = React.useMemo(() => {
    const codes =
      businessTripConfig?.priorityCountries?.map((c) => c.code.toLowerCase()) ??
      []
    if (codes.length > 0) return codes
    return ['pl', 'de', 'fr', 'at', 'be', 'cz', 'es', 'fr']
  }, [businessTripConfig])
  const countries = React.useMemo(() => {
    const source =
      Array.isArray(countriesProp) && countriesProp.length > 0
        ? countriesProp
        : (fetchedCountries.map((c: any) => ({
            ...c,
            id: String(c.id),
          })) as Array<CountryModel>)

    const seen = new Set<string>()
    return source.filter((c) => {
      const key = `${(c.code ?? '').toLowerCase()}|${(c.name ?? '')
        .trim()
        .toLowerCase()}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [countriesProp, fetchedCountries])
  const [date, setDate] = React.useState<Moment>(moment())
  const [city, setCity] = React.useState<string | null>(null)
  const [country, setCountry] = React.useState<CountryModel | null>(null)
  const [events, setEvents] = React.useState<Array<EventModel>>(propsEvents)
  const [isTransit, setIsTransit] = React.useState<boolean>(false)
  const [changed, setChanged] = React.useState<boolean>(false)
  const [docNumber, setDocNumber] = React.useState<string | null>(null)
  const [docNumberEdited, setDocNumberEdited] = React.useState<boolean>(false)
  const [secondDate, setSecondDate] = React.useState<Moment | null>(null)
  const [secondCountry, setSecondCountry] = React.useState<CountryModel | null>(
    null,
  )
  const [routeType, setRouteType] = React.useState<TmsRouteType>(
    TmsRouteType.Undefined,
  )
  const [eventType, setEventType] = React.useState<TripEventType>(
    delegationMarker?.tripEvent ?? TripEventType.NotSet,
  )
  const [
    showBusinessTripContinuationDialog,
    setShowBusinessTripContinuationDialog,
  ] = React.useState<boolean>(false)
  const [addItemsFromPreviousTrip, setAddItemsFromPreviousTrip] =
    React.useState<boolean>(false)
  const [shouldAddItemsFromPreviousTrip, setShouldAddItemsFromPreviousTrip] =
    React.useState<boolean>(false)
  const [newMarkers, setNewMarkers] = React.useState<Array<EventModel>>([])
  const [beginCountry, setBeginCountry] = React.useState<CountryModel | null>(
    null,
  )
  const [submitted, setSubmitted] = React.useState<boolean>(false)
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)

  const docNumberNotEmptyValid = isDocNumberNotEmpty(docNumber, eventType)
  const docNumberUniqueValid = isDocNumberValid(
    docNumber,
    eventType,
    events,
    delegationMarker,
  )

  const hasCounterpart = React.useMemo(
    () =>
      isTmsEvent(delegationMarker) &&
      !!delegationMarker.docNumber &&
      propsEvents.some(
        (e) =>
          isTmsEvent(e) &&
          e.docNumber === delegationMarker.docNumber &&
          e.tripEvent !== delegationMarker.tripEvent,
      ),
    [delegationMarker, propsEvents],
  )

  const countriesOptions = React.useMemo(
    () =>
      Array.isArray(countries)
        ? countries.map((c) => ({
            label: translateCountryName(c.name, lang),
            value: c.id.toString(),
          }))
        : [],
    [countries, lang],
  )

  React.useEffect(() => {
    if (delegationMarker == null) return

    const {
      date: markerDate,
      country: markerCountry,
      city: markerCity,
      isTransit: markerIsTransit,
      docNumber: markerDocNumber,
      secondDate: markerSecondDate,
      secondCountry: markerSecondCountry,
      tmsRouteType,
      isNew,
      tripEvent,
      temporary,
    } = delegationMarker

    const momentDate = markerDate
      ? moment.isMoment(markerDate)
        ? markerDate
        : moment(markerDate)
      : moment()

    const momentSecondDate = markerSecondDate
      ? moment.isMoment(markerSecondDate)
        ? markerSecondDate
        : moment(markerSecondDate)
      : null

    setDate(momentDate)
    setCountry(markerCountry)
    setCity(markerCity)
    setIsTransit(markerIsTransit)
    setDocNumber(markerDocNumber)
    setSecondDate(momentSecondDate)
    setSecondCountry(markerSecondCountry)
    setRouteType(tmsRouteType)

    setDocNumberEdited(false)

    if (isNew && isTmsEvent(delegationMarker)) {
      const countryChange = findPrecedingCountryChange(propsEvents, momentDate)
      if (countryChange) setCountry(countryChange.country)
    }

    const allEvents = [...allPreviousTripEvents, ...propsEvents]
    if (
      isNew &&
      temporary &&
      allEvents.length > 0 &&
      tripEvent === TripEventType.Begin
    ) {
      const sortedEvents = [...allEvents].sort((a, b) => {
        if (a.date < b.date) return 1
        if (a.date > b.date) return -1
        return 0
      })

      let loadingItem = sortedEvents.find(
        (e) => e.tripEvent === TripEventType.Loading && e.date < momentDate,
      )

      if (loadingItem != null) {
        const unloadingItem = sortedEvents.find(
          (e) =>
            e.id === loadingItem.id &&
            e.tripEvent === TripEventType.Unloading &&
            e.docNumber === loadingItem.docNumber &&
            e.date > momentDate,
        )

        if (
          unloadingItem != null &&
          sortedEvents.some(
            (e) =>
              e.id === loadingItem.id &&
              e.tripEvent === TripEventType.Finish &&
              e.date < momentDate,
          )
        ) {
          let lastCountryItem = sortedEvents.find(
            (e) =>
              e.id === unloadingItem.id &&
              e.tripEvent === TripEventType.ChangeCountry &&
              e.date < momentDate,
          )
          if (lastCountryItem == null) {
            lastCountryItem = unloadingItem
          }

          let finishItem = null
          for (let i = propsEvents.length - 1; i >= 0; i--) {
            if (propsEvents[i].date < momentDate) {
              break
            }
            if (propsEvents[i].tripEvent === TripEventType.Finish) {
              finishItem = propsEvents[i]
            }
            if (propsEvents[i].tripEvent === TripEventType.Begin) {
              finishItem = null
            }
          }

          if (
            !(
              (finishItem == null ||
                (finishItem as any).EventDate > unloadingItem.date) &&
              (propsEvents == null ||
                !propsEvents.some(
                  (ci) =>
                    !ci.isNew &&
                    ci.tripEvent === TripEventType.Begin &&
                    ci.date > loadingItem.date &&
                    ci.date < unloadingItem.date,
                ))
            )
          ) {
            loadingItem = null
          }

          if (loadingItem != null) {
            loadingItem.secondCountry = unloadingItem.country
            loadingItem.secondDate = unloadingItem.date

            const changeCountryItems = allPreviousTripEvents.filter(
              (e) =>
                e.tripEvent === TripEventType.ChangeCountry &&
                e.date > momentDate &&
                e.date < unloadingItem.date,
            )
            setNewMarkers([loadingItem, ...changeCountryItems])
            setBeginCountry(lastCountryItem.country)
            setShowBusinessTripContinuationDialog(true)
            setShouldAddItemsFromPreviousTrip(
              !propsEvents.some(
                (e) =>
                  e.tripEvent === TripEventType.Loading &&
                  e.docNumber === loadingItem.docNumber,
              ),
            )
          }
        }
      }
    }
  }, [delegationMarker, propsEvents, allPreviousTripEvents])

  const suggestsDocNumber =
    !!delegationMarker?.isNew &&
    !readOnly &&
    delegationMarker.tripEvent === TripEventType.Unloading &&
    !delegationMarker.docNumber

  // Only the number: filled second data would mean "add a counterpart" on save.
  React.useEffect(() => {
    if (suggestsDocNumber && !docNumberEdited && date) {
      setDocNumber(findOpenLoading(propsEvents, date)?.docNumber ?? null)
    }
  }, [suggestsDocNumber, docNumberEdited, date, propsEvents])

  // Sync events from props
  React.useEffect(() => {
    if (propsEvents != null && propsEvents !== events) {
      setEvents(propsEvents)
    }
  }, [propsEvents])

  // Sync event type
  React.useEffect(() => {
    if (
      delegationMarker?.tripEvent != null &&
      delegationMarker.tripEvent !== eventType
    ) {
      setEventType(delegationMarker.tripEvent)
    }
  }, [delegationMarker?.tripEvent])

  // Allow scroll propagation through popup
  React.useEffect(() => {
    if (!isOpen || !popoverRef.current) return undefined

    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement
      const popoverContent = popoverRef.current

      if (!popoverContent?.contains(target)) return

      // Check if we're scrolling inside the popup content
      const scrollableParent = target.closest(
        '.tw-overflow-auto, .tw-overflow-y-auto',
      )

      if (scrollableParent) {
        // If there's scrollable content inside popup, allow it to scroll
        const { scrollTop, scrollHeight, clientHeight } = scrollableParent
        const isAtTop = scrollTop === 0
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1

        if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
          // At scroll boundary, allow propagation to background
          return
        }
        // Otherwise stop propagation (scroll inside popup)
        e.stopPropagation()
      }
      // If no scrollable content, allow scroll to propagate to background
    }

    const popoverElement = popoverRef.current
    popoverElement.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      popoverElement.removeEventListener('wheel', handleWheel)
    }
  }, [isOpen])

  // Date change handlers
  const handleDateChange = React.useCallback(
    (value: string | null, isSecond: boolean = false) => {
      if (!value) {
        if (isSecond) {
          // The counterpart's date is not removable from here; the counterpart itself is.
          if (hasCounterpart) return
          setSecondDate(null)
        } else {
          setDate(null)
        }
        setChanged(true)
        return
      }

      const currentMoment = isSecond ? (secondDate ?? date) : date
      const currentTime = currentMoment
        ? currentMoment.format('HH:mm')
        : '00:00'
      const momentDate = moment(`${value} ${currentTime}`, 'YYYY-MM-DD HH:mm')

      if (isSecond) {
        setSecondDate(momentDate)
      } else {
        setDate(momentDate)
      }
      setChanged(true)
    },
    [date, secondDate, hasCounterpart],
  )

  const handleTimeChange = React.useCallback(
    (value: string | null, isSecond: boolean = false) => {
      if (!value) return

      const currentMoment = isSecond ? (secondDate ?? date) : date
      const currentDate = currentMoment
        ? currentMoment.format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD')
      const momentDate = moment(`${currentDate} ${value}`, 'YYYY-MM-DD HH:mm')

      if (isSecond) {
        setSecondDate(momentDate)
      } else {
        setDate(momentDate)
      }
      setChanged(true)
    },
    [date, secondDate],
  )

  const commitFieldOnEnter = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.currentTarget.blur()
      }
    },
    [],
  )

  const updateTmsRouteType = React.useCallback(
    (newCountry: CountryModel, newSecondCountry: CountryModel) => {
      // With one country unknown there is nothing to derive; the stored type stays.
      if (newCountry == null || newSecondCountry == null) return
      if (
        delegationMarker.tripEvent === TripEventType.Loading ||
        delegationMarker.tripEvent === TripEventType.Unloading
      ) {
        const newRouteType = determineRouteType(
          newCountry,
          newSecondCountry,
          delegationMarker.tripEvent === TripEventType.Loading,
        )
        setRouteType(newRouteType)
      }
    },
    [delegationMarker.tripEvent],
  )

  const handleCountryChange = React.useCallback(
    (value: string, isSecond: boolean = false) => {
      const selectedCountry = countries.find((c) => c.id.toString() === value)
      if (!selectedCountry) return

      if (isSecond) {
        setSecondCountry(selectedCountry)
        updateTmsRouteType(country, selectedCountry)
      } else {
        setCountry(selectedCountry)
        updateTmsRouteType(selectedCountry, secondCountry)
      }
      setChanged(true)
    },
    [countries, country, secondCountry, updateTmsRouteType],
  )

  const handleFlagClick = React.useCallback(
    (code: string, isUnloading: boolean) => {
      if (readOnly) return
      const selectedCountry = countries.find(
        (c) => c.code.toUpperCase() === code.toUpperCase(),
      )
      if (!selectedCountry) return

      if (isUnloading) {
        setSecondCountry(selectedCountry)
        updateTmsRouteType(country, selectedCountry)
      } else {
        setCountry(selectedCountry)
        updateTmsRouteType(selectedCountry, secondCountry)
      }
      setChanged(true)
    },
    [readOnly, countries, country, secondCountry, updateTmsRouteType],
  )

  const save = () => {
    setSubmitted(true)

    if (!docNumberNotEmptyValid || !docNumberUniqueValid) {
      return
    }

    closePopup(
      {
        ...delegationMarker,
        isNew: false,
        temporary: false,
        city,
        country,
        date,
        isTransit,
        docNumber,
        tmsRouteType: routeType,
        secondDate,
        secondCountry,
      },
      true,
      changed,
      addItemsFromPreviousTrip && shouldAddItemsFromPreviousTrip
        ? newMarkers
        : [],
    )
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    save()
  }

  const saveRef = React.useRef(save)
  saveRef.current = save

  React.useEffect(() => {
    if (isOpen) {
      contentRef.current?.focus()
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || readOnly) return undefined

    const handleEnterSave = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      if (showBusinessTripContinuationDialog) return

      const target = e.target as HTMLElement | null
      if (
        !target ||
        !contentRef.current ||
        !contentRef.current.contains(target)
      ) {
        return
      }
      if (target.closest('button, a, [role="combobox"]')) return

      e.preventDefault()
      saveRef.current()
    }

    document.addEventListener('keydown', handleEnterSave)
    return () => document.removeEventListener('keydown', handleEnterSave)
  }, [isOpen, readOnly, showBusinessTripContinuationDialog])

  const renderFlag = (code: string, isUnloading: boolean) => {
    let isSelected = false
    if (isUnloading) {
      isSelected =
        secondCountry != null &&
        secondCountry.code.toUpperCase() === code.toUpperCase()
    } else {
      isSelected =
        country != null && country.code.toUpperCase() === code.toUpperCase()
    }

    return (
      <button
        key={code}
        type="button"
        disabled={readOnly}
        onClick={() => handleFlagClick(code, isUnloading)}
        className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-bg-transparent tw-p-1.5 tw-transition-colors ${
          isSelected
            ? 'tw-border-primary tw-bg-base-200'
            : 'tw-border-transparent hover:tw-bg-base-200'
        } ${readOnly ? 'tw-cursor-not-allowed tw-opacity-50' : ''}`}
      >
        <img src={getCountryFlag(code)} width={24} height={20} alt={code} />
        <span className="tw-mt-0.5 tw-text-xs tw-font-semibold tw-text-content-base">
          {code.toUpperCase()}
        </span>
      </button>
    )
  }

  const renderBusinessTripContinuationDialog = () => {
    return (
      <ModalLayout customZIndex={9001}>
        <div
          className="tw-flex tw-max-h-[90vh] tw-w-full tw-max-w-[500px] tw-flex-col tw-rounded tw-bg-white"
          onClick={(event) => event.stopPropagation()}
        >
          <div
            className="tw-flex tw-items-center tw-gap-5 tw-rounded-t tw-px-4 tw-py-4"
            style={{ background: '#FFA928', color: '#fff' }}
          >
            <AdminIcon name="toast_warning" />
            <span style={{ color: '#fff', fontSize: 16 }}>
              {translate('businessTripContinuationDialog.title')}
            </span>
          </div>
          <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
          <FlexView
            column
            vAlignContent="center"
            hAlignContent="center"
            style={{ padding: 5 }}
          >
            <FlexView hAlignContent="center" column>
              <span>
                {translate('businessTripContinuationDialog.text1')}
                <span
                  style={{ fontWeight: 500, marginLeft: 5, marginRight: 5 }}
                >
                  {newMarkers
                    .find((m) => m.tripEvent === TripEventType.Loading)
                    ?.secondDate?.format(TIME_FORMAT)}
                </span>
                <span>
                  {translate('businessTripContinuationDialog.text2')}
                  <img
                    style={{ marginLeft: 5, marginTop: -3 }}
                    src={getCountryFlag(
                      newMarkers
                        .find((m) => m.tripEvent === TripEventType.Loading)
                        ?.secondCountry?.code?.toUpperCase() === 'GB'
                        ? 'EN'
                        : newMarkers.find(
                            (m) => m.tripEvent === TripEventType.Loading,
                          )?.secondCountry?.code,
                    )}
                    width={18}
                    height={18}
                  />
                  <span
                    style={{ fontWeight: 500, marginLeft: 5, marginRight: 5 }}
                  >
                    {
                      newMarkers.find(
                        (m) => m.tripEvent === TripEventType.Loading,
                      )?.secondCountry?.name
                    }
                  </span>
                </span>
              </span>
              <span style={{ textAlign: 'center' }}>
                {translate('businessTripContinuationDialog.text3')}
              </span>
              <span>
                {translate('businessTripContinuationDialog.text5')}
                <span
                  style={{ fontWeight: 500, marginLeft: 5, marginRight: 5 }}
                >
                  {newMarkers
                    .find((m) => m.tripEvent === TripEventType.Loading)
                    ?.date?.format(TIME_FORMAT)}
                </span>
                {translate('businessTripContinuationDialog.text2')}
                <img
                  style={{ marginLeft: 5, marginTop: -3 }}
                  src={getCountryFlag(
                    newMarkers
                      .find((m) => m.tripEvent === TripEventType.Loading)
                      ?.country?.code?.toUpperCase() === 'GB'
                      ? 'EN'
                      : newMarkers.find(
                          (m) => m.tripEvent === TripEventType.Loading,
                        )?.country?.code,
                  )}
                  width={18}
                  height={18}
                />
                <span
                  style={{ fontWeight: 500, marginLeft: 5, marginRight: 5 }}
                >
                  {
                    newMarkers.find(
                      (m) => m.tripEvent === TripEventType.Loading,
                    )?.country?.name
                  }
                </span>
              </span>
              <span style={{ marginTop: 20 }}>
                {translate('businessTripContinuationDialog.text4')}
                <img
                  style={{ marginLeft: 5, marginTop: -3 }}
                  src={getCountryFlag(
                    beginCountry?.code?.toUpperCase() === 'GB'
                      ? 'EN'
                      : beginCountry?.code,
                  )}
                  width={18}
                  height={18}
                />
                <span
                  style={{ fontWeight: 500, marginLeft: 5, marginRight: 5 }}
                >
                  {beginCountry?.name}
                </span>
                <span>?</span>
              </span>
            </FlexView>
            <FlexView
              vAlignContent="center"
              hAlignContent="center"
              style={{ marginTop: 15 }}
            >
              <FlexView
                vAlignContent="center"
                hAlignContent="center"
                style={{
                  width: 50,
                  height: 30,
                  background: '#FFA928',
                  cursor: 'pointer',
                }}
                onClick={() => setShowBusinessTripContinuationDialog(false)}
              >
                <span
                  style={{ color: '#fff', fontSize: 14, cursor: 'pointer' }}
                >
                  {translate('answer.no')}
                </span>
              </FlexView>
              <FlexView
                vAlignContent="center"
                hAlignContent="center"
                style={{
                  marginLeft: 10,
                  width: 50,
                  height: 30,
                  background: '#FFA928',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setShowBusinessTripContinuationDialog(false)
                  setAddItemsFromPreviousTrip(true)
                  setCountry(beginCountry)
                }}
              >
                <span
                  style={{ color: '#fff', fontSize: 14, cursor: 'pointer' }}
                >
                  {translate('answer.yes')}
                </span>
              </FlexView>
            </FlexView>
          </FlexView>
          </div>
        </div>
      </ModalLayout>
    )
  }

  const isExtended =
    delegationMarker.tripEvent === TripEventType.Loading ||
    delegationMarker.tripEvent === TripEventType.Unloading

  const showCountryField =
    delegationMarker.tripEvent === TripEventType.ChangeCountry ||
    delegationMarker.tripEvent === TripEventType.Loading ||
    delegationMarker.tripEvent === TripEventType.Unloading ||
    delegationMarker.tripEvent === TripEventType.Begin ||
    delegationMarker.tripEvent === TripEventType.Finish

  const showCityField =
    delegationMarker.tripEvent !== TripEventType.TripExclusionStart &&
    delegationMarker.tripEvent !== TripEventType.TripExclusionEnd &&
    delegationMarker.tripEvent !== TripEventType.Loading &&
    delegationMarker.tripEvent !== TripEventType.Unloading

  const showTransitField =
    delegationMarker.tripEvent !== TripEventType.Loading &&
    delegationMarker.tripEvent !== TripEventType.Unloading

  return (
    <div ref={popoverRef} className="tw-z-[9000]">
      <PortalPopover
        isOpen={isOpen}
        targetId={id}
        className={
          isExtended
            ? 'delegation-marker-extended tw-modern-popup'
            : 'delegation-marker tw-modern-popup'
        }
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-rounded-t-lg tw-border-b tw-border-base-300 tw-bg-base-200 tw-px-4 tw-py-3">
          <span className="tw-text-base tw-font-semibold tw-text-content-base">
            {getTripEventTypeName(delegationMarker.tripEvent, translate)}
          </span>
          <div className="tw-flex tw-items-center tw-gap-1">
            {!readOnly && (
              <button
                type="button"
                onClick={() => onDelete(delegationMarker)}
                className="tw-text-content-muted tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border-none tw-bg-transparent hover:tw-bg-base-300 hover:tw-text-error"
                title={translate('operation.delete')}
              >
                <FaIcon icon={faTrash} className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
        </div>

        <div
          ref={contentRef}
          tabIndex={-1}
          className="tw-p-0 focus:tw-outline-none"
        >
          <div
            className={`tw-flex ${isExtended ? 'tw-flex-row' : 'tw-flex-col'}`}
          >
            {/* Left column */}
            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
              {/* Doc Number (Loading/Unloading only) */}
              {isExtended && (
                <div>
                  <label className="tw-text-content-muted tw-mb-1 tw-block tw-text-xs tw-font-medium">
                    {translate('newDelegation.delegationMarkerPopup.docNumber')}
                  </label>
                  <input
                    type="text"
                    value={docNumber || ''}
                    onChange={(e) => {
                      setDocNumber(e.target.value)
                      setDocNumberEdited(true)
                      setChanged(true)
                    }}
                    readOnly={readOnly}
                    className={`tw-block tw-w-[200px] tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 tw-text-sm ${
                      submitted && !docNumberNotEmptyValid
                        ? 'tw-border-error'
                        : submitted && !docNumberUniqueValid
                          ? 'tw-border-error'
                          : 'tw-border-gray-300'
                    } focus:tw-border-primary focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary`}
                  />
                  {submitted && !docNumberNotEmptyValid && (
                    <span className="tw-mt-1 tw-block tw-text-xs tw-text-error">
                      {translate(
                        'newDelegation.delegationMarkerPopup.validation-doc-number-empty',
                      )}
                    </span>
                  )}
                  {submitted && !docNumberUniqueValid && (
                    <span className="tw-mt-1 tw-block tw-text-xs tw-text-error">
                      {translate(
                        'newDelegation.delegationMarkerPopup.validation-doc-number-duplicated',
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* Date & Time Fields */}
              <div>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <DateField
                    label={
                      translate(
                        'newDelegation.delegationMarkerPopup.eventDate',
                      ) || 'Data'
                    }
                    value={date ? date.format('YYYY-MM-DD') : null}
                    setValue={(v) => handleDateChange(v, false)}
                    disabled={readOnly}
                  />
                  <TimeField
                    label={
                      translate(
                        'newDelegation.delegationMarkerPopup.eventTime',
                      ) || 'Godzina'
                    }
                    value={date ? date.format('HH:mm') : null}
                    setValue={(v) => handleTimeChange(v, false)}
                    onKeyDown={commitFieldOnEnter}
                    disabled={readOnly}
                  />
                </div>
              </div>

              {/* Country Select */}
              {showCountryField && (
                <div>
                  <FieldsetSelect
                    label={
                      (delegationMarker.tripEvent ===
                        TripEventType.ChangeCountry ||
                        delegationMarker.tripEvent === TripEventType.Begin ||
                        delegationMarker.tripEvent === TripEventType.Finish) &&
                      translate('newDelegation.delegationMarkerPopup.country')
                        ? translate(
                            'newDelegation.delegationMarkerPopup.country',
                          )
                        : delegationMarker.tripEvent ===
                              TripEventType.Loading &&
                            translate(
                              'newDelegation.delegationMarkerPopup.loadingCountry',
                            )
                          ? translate(
                              'newDelegation.delegationMarkerPopup.loadingCountry',
                            )
                          : delegationMarker.tripEvent ===
                              TripEventType.Unloading
                            ? translate(
                                'newDelegation.delegationMarkerPopup.unloadingCountry',
                              )
                            : translate('businessTrip.country')
                    }
                    placeholder={translate('operation.find')}
                    value={country?.id?.toString() ?? ''}
                    name="country"
                    onChange={(e) => handleCountryChange(e.target.value, false)}
                    options={countriesOptions}
                    disabled={readOnly}
                  />
                </div>
              )}

              {/* Quick country flags */}
              {showCountryField && (
                <div>
                  <label className="tw-text-content-muted tw-mb-2 tw-block tw-text-xs tw-font-medium">
                    {translate(
                      'newDelegation.delegationMarkerPopup.oftenChosen',
                    )}
                  </label>
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {Array.from(
                      { length: Math.ceil(priorityCountryCodes.length / 4) },
                      (_, i) => (
                        <div key={i} className="tw-flex tw-gap-1">
                          {priorityCountryCodes
                            .slice(i * 4, i * 4 + 4)
                            .map((code) => renderFlag(code, false))}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* City input */}
              {showCityField && (
                <div>
                  <label className="tw-text-content-muted tw-mb-1 tw-block tw-text-xs tw-font-medium">
                    {translate('newDelegation.delegationMarkerPopup.city')}
                  </label>
                  <input
                    type="text"
                    value={city || ''}
                    onChange={(e) => {
                      setCity(e.target.value)
                      setChanged(true)
                    }}
                    readOnly={readOnly}
                    className="tw-block tw-w-[200px] tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary"
                  />
                </div>
              )}

              {/* Transit checkbox */}
              {showTransitField && (
                <CheckboxWithLabelField
                  id={'dm_popup_transit_' + delegationMarker.lp}
                  value={isTransit}
                  onChange={(checked) => {
                    setIsTransit(checked)
                    setChanged(true)
                  }}
                  disabled={readOnly}
                  label={translate('newDelegation.events-transit')}
                />
              )}
            </div>

            {/* Right column (Loading/Unloading only) */}
            {isExtended && (
              <div className="tw-flex tw-flex-col tw-gap-4 tw-border-l tw-border-gray-200 tw-p-4">
                {/* Route type */}
                <div>
                  <label className="tw-text-content-muted tw-mb-1 tw-block tw-text-xs tw-font-medium">
                    {translate('newDelegation.delegationMarkerPopup.routeType')}
                  </label>
                  <input
                    type="text"
                    value={translate(getTmsRouteTypeName(routeType))}
                    readOnly
                    style={{ color: getTmsRouteTypeColor(routeType) }}
                    className="tw-block tw-w-[200px] tw-rounded-md tw-border tw-border-gray-300 tw-bg-gray-100 tw-px-3 tw-py-2 tw-text-sm tw-font-medium"
                  />
                </div>

                {/* Second date & time */}
                <div>
                  <label className="tw-text-content-muted tw-mb-1 tw-block tw-text-xs tw-font-medium">
                    {delegationMarker.tripEvent === TripEventType.Loading &&
                      translate(
                        'newDelegation.delegationMarkerPopup.unloading',
                      )}
                    {delegationMarker.tripEvent === TripEventType.Unloading &&
                      translate('newDelegation.delegationMarkerPopup.loading')}
                  </label>
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <DateField
                      label={
                        translate(
                          'newDelegation.delegationMarkerPopup.eventDate',
                        ) || 'Data'
                      }
                      value={secondDate ? secondDate.format('YYYY-MM-DD') : null}
                      initialDate={date ? date.format('YYYY-MM-DD') : null}
                      clearable={!hasCounterpart}
                      setValue={(v) => handleDateChange(v, true)}
                      disabled={readOnly}
                    />
                    <TimeField
                      label={
                        translate(
                          'newDelegation.delegationMarkerPopup.eventTime',
                        ) || 'Godzina'
                      }
                      value={secondDate ? secondDate.format('HH:mm') : null}
                      setValue={(v) => handleTimeChange(v, true)}
                      onKeyDown={commitFieldOnEnter}
                      disabled={readOnly}
                    />
                  </div>
                </div>

                {/* Second country */}
                <div>
                  <FieldsetSelect
                    label={
                      delegationMarker.tripEvent === TripEventType.Loading
                        ? translate(
                            'newDelegation.delegationMarkerPopup.unloadingCountry',
                          )
                        : translate(
                            'newDelegation.delegationMarkerPopup.loadingCountry',
                          )
                    }
                    placeholder={translate('operation.find')}
                    value={secondCountry?.id?.toString() ?? ''}
                    name="secondCountry"
                    onChange={(e) => handleCountryChange(e.target.value, true)}
                    options={countriesOptions}
                    disabled={readOnly}
                  />
                </div>

                {/* Second quick country flags */}
                <div>
                  <label className="tw-text-content-muted tw-mb-2 tw-block tw-text-xs tw-font-medium">
                    {translate(
                      'newDelegation.delegationMarkerPopup.oftenChosen',
                    )}
                  </label>
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    {Array.from(
                      { length: Math.ceil(priorityCountryCodes.length / 4) },
                      (_, i) => (
                        <div key={i} className="tw-flex tw-gap-1">
                          {priorityCountryCodes
                            .slice(i * 4, i * 4 + 4)
                            .map((code) => renderFlag(code, true))}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer buttons */}
          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-base-300 tw-p-3">
            {!delegationMarker.temporary && (
              <button
                type="button"
                onClick={() => closePopup(delegationMarker, false, changed, [])}
                className="tw-cursor-pointer tw-rounded-lg tw-bg-secondary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-primary hover:tw-opacity-80"
              >
                {translate('operation.close')}
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={handleFormSubmit}
                className="tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80"
              >
                {translate('operation.save')}
              </button>
            )}
          </div>
        </div>
      </PortalPopover>
      {showBusinessTripContinuationDialog &&
        renderBusinessTripContinuationDialog()}
    </div>
  )
}
